/**
 * What the game is allowed to know about the network.
 *
 * One contract, two implementations: [loopbackTransport](./loopbackTransport.ts) in memory and
 * [trysteroTransport](./trysteroTransport.ts) over WebRTC. Single-player and the tests run the
 * loopback, so there is no path through the game that only multiplayer exercises, and no "if we are
 * online" branch anywhere above this layer.
 *
 * Two channels, because snapshots and events want opposite things. A snapshot is worthless the
 * moment a newer one exists, so it rides an unreliable, unordered channel and a lost one is simply
 * skipped. A lobby or inventory event has to arrive, exactly once and in order, so it rides
 * Trystero's own reliable channel. Nothing else is offered: a third guarantee would be a third set
 * of bugs.
 *
 * Sending takes a payload and a kind rather than bytes, so framing is not something a caller can
 * forget. See [frameCodec](./wire/frameCodec.ts).
 */

import type { DecodedFrame } from '@/net/wire/frameCodec';
import type { MessageKind } from '@/net/wire/messageKinds';
import type { NetError, NetErrorCode } from '@/net/session/sessionErrors';

export type PeerId = string;

/** The host creates the session and is the centre of the star. Everyone else is a guest. */
export type SessionRole = 'host' | 'guest';

/** `snapshot` is unreliable and unordered, `event` is reliable and ordered. */
export type Channel = 'snapshot' | 'event';

export type ConnectionPhase =
  'idle' | 'connecting' | 'connected' | 'reconnecting' | 'failed' | 'closed';

export interface ConnectionStatus {
  readonly phase: ConnectionPhase;
  /** One sentence written for a player. Safe to render as-is, in the lobby or anywhere else. */
  readonly detail: string;
  readonly code: NetErrorCode | null;
  /** Which signaling strategy carried this session, once one did. */
  readonly strategyId: string | null;
}

export interface TransportPeer {
  readonly id: PeerId;
  readonly role: SessionRole;
  /** False means this peer's snapshots are riding the reliable channel instead. */
  readonly snapshotChannelReady: boolean;
  /** Null until the first stats sample. True means the pair is going through TURN. */
  readonly relayed: boolean | null;
}

export interface TransportListeners {
  onStatus(status: ConnectionStatus): void;
  onPeerJoin(peer: TransportPeer): void;
  onPeerUpdate(peer: TransportPeer): void;
  onPeerLeave(id: PeerId, reason: string): void;
  /** The frame is a view into the receive buffer. Copy anything kept past this call. */
  onMessage(from: PeerId, frame: DecodedFrame, channel: Channel): void;
  onError(error: NetError): void;
}

export interface Transport {
  readonly selfId: PeerId;
  readonly role: SessionRole;
  /** Grouped for reading aloud: `RUST-7F2K`. The loopback reports `LOCAL`. */
  readonly joinCode: string;
  status(): ConnectionStatus;
  peers(): readonly TransportPeer[];
  /**
   * The heartbeat's last round trip to a peer, or null before it has answered one.
   *
   * Sampled rather than announced, unlike everything on `TransportPeer`. A fresh number every
   * second is not a change anybody has to be told about, and pushing it through `onPeerUpdate`
   * would wake every screen watching this session once a peer per second to redraw the same row.
   * See [peerHeartbeat](./session/peerHeartbeat.ts).
   */
  rttOf(peerId: PeerId): number | null;
  /**
   * Frames the payload and sends it. A snapshot to a peer whose unreliable channel never opened
   * goes out on the reliable one instead, degraded rather than dropped. Omitting `target`
   * broadcasts.
   */
  send(channel: Channel, kind: MessageKind, payload: Uint8Array, target?: PeerId): void;
  close(reason: string): Promise<void>;
}

export interface TransportOpenOptions {
  /** What the player typed, in any shape. Ignored when hosting, where a code is generated. */
  readonly code: string | null;
  readonly role: SessionRole;
  readonly listeners: TransportListeners;
  /**
   * The tokens of the seats being kept for players who dropped and may yet come back.
   *
   * The transport counts live peers, and that is all it can see. A held seat has no peer and still
   * has an owner, so the count that decides whether a session is full has to come from whoever is
   * keeping it. The tokens rather than the count, because one of those seats may belong to the
   * guest at the door: a returning player proves which, and is let in against its own chair rather
   * than a fifth one. Absent means nobody is holding anything.
   */
  readonly heldTokens?: () => readonly string[];
  /**
   * What this side was seated with on this code before, or null.
   *
   * Read at handshake time and never sent as it is: it is the secret half of the proof above. A
   * host never has one, because a host does not come back to a seat.
   */
  readonly rejoinToken?: () => string | null;
}

export type TransportFactory = (options: TransportOpenOptions) => Promise<Transport>;

/** Listeners that do nothing, so a caller can supply only the ones it cares about. */
export function silentListeners(): TransportListeners {
  return {
    onStatus: (): void => undefined,
    onPeerJoin: (): void => undefined,
    onPeerUpdate: (): void => undefined,
    onPeerLeave: (): void => undefined,
    onMessage: (): void => undefined,
    onError: (): void => undefined,
  };
}
